import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Stack } from "@mui/material";
import {
  Close as CloseIcon,
  Description as DescriptionIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { colors } from "../theme/colors";
import type { FileAttachment } from "./AIChatTypes";
import { CustomFileNameDisplay } from "../TruncateText/FileName/CustomFileNameDisplay";
import { CustomFileModal } from "@ippon/shared-components";

interface FilePreviewProps {
  file: FileAttachment;
  onRemove?: () => void;
  primaryColor?: string;
  showImagePreview?: boolean;
  variant?: "compact" | "imageCard";
}

export const FilePreview: React.FC<FilePreviewProps> = ({
  file,
  onRemove,
  primaryColor = colors.primary.main,
  showImagePreview = false,
  variant = "compact",
}) => {
  const canOpen = !onRemove && (!!file.url || !!file.file);
  const [localObjectUrl, setLocalObjectUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const isImageFile = useMemo(() => {
    if (file.file?.type) return file.file.type.startsWith("image/");
    if (file.type) return file.type.startsWith("image/");
    const nameOrUrl = (file.name || file.url || "").toLowerCase();
    return /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/.test(nameOrUrl);
  }, [file.file, file.type, file.name, file.url]);

  const isPdfFile = useMemo(() => {
    if (file.file?.type) return file.file.type === "application/pdf";
    if (file.type) return file.type === "application/pdf";
    const nameOrUrl = (file.name || file.url || "").toLowerCase();
    return /\.pdf(\?.*)?$/.test(nameOrUrl);
  }, [file.file, file.type, file.name, file.url]);

  useEffect(() => {
    if (!isImageFile && !isPdfFile) return;
    if (!file.file) return;

    const url = URL.createObjectURL(file.file);
    requestAnimationFrame(() => {
      setLocalObjectUrl(url);
    });
    return () => {
      URL.revokeObjectURL(url);
      requestAnimationFrame(() => {
        setLocalObjectUrl(null);
      });
    };
  }, [file.file, isImageFile, isPdfFile, showImagePreview]);

  const previewSrc = file.url || localObjectUrl;
  const shouldShowImageCard =
    showImagePreview && variant === "imageCard" && isImageFile && !!previewSrc;

  const handleFileClick = () => {
    if (!canOpen) return;
    if (isPdfFile || isImageFile) {
      setPreviewOpen(true);
    } else if (file.url) {
      window.open(file.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  if (shouldShowImageCard) {
    return (
      <Box
        sx={{
          position: "relative",
          height: 50,
          aspectRatio: "1 / 1",
          borderRadius: "10px",
          overflow: "hidden",
          border: "1px solid #E6E6E6",
          backgroundColor: colors.background.light,
          cursor: canOpen ? "pointer" : "default",
        }}
        role={canOpen ? "button" : undefined}
        tabIndex={canOpen ? 0 : undefined}
        onClick={handleFileClick}
        onKeyDown={(e) => {
          if (!canOpen) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleFileClick();
          }
        }}
      >
        <Box
          component="img"
          src={previewSrc}
          alt={file.name}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            backgroundColor: colors.background.light,
          }}
          loading="lazy"
          referrerPolicy="no-referrer"
        />

        <Box
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            display: "flex",
            gap: 0.5,
          }}
        >
          {canOpen && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                if (file.url)
                  window.open(file.url, "_blank", "noopener,noreferrer");
              }}
              size="small"
              sx={{
                width: 28,
                height: 28,
                backgroundColor: "rgba(0,0,0,0.55)",
                color: "#FFFFFF",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.65)" },
              }}
              aria-label="別タブで開く"
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}

          {onRemove && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              size="small"
              sx={{
                width: 20,
                height: 20,
                backgroundColor: "rgba(0,0,0,0.6)",
                color: "#FFFFFF",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
              }}
              aria-label="削除"
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>

        {canOpen && previewSrc && (
          <CustomFileModal
            open={previewOpen}
            onClose={handleClosePreview}
            imageSrc={previewSrc}
            imageName={file.name}
          />
        )}
      </Box>
    );
  }

  if (isImageFile && previewSrc) {
    return (
      <Box
        sx={{
          position: "relative",
          width: 50,
          height: 50,
          borderRadius: "10px",
          overflow: "hidden",
          border: "1px solid #E6E6E6",
          backgroundColor: colors.background.light,
          cursor: canOpen ? "pointer" : "default",
          flexShrink: 0,
        }}
        role={canOpen ? "button" : undefined}
        tabIndex={canOpen ? 0 : undefined}
        onClick={handleFileClick}
        onKeyDown={(e) => {
          if (!canOpen) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleFileClick();
          }
        }}
      >
        <Box
          component="img"
          src={previewSrc}
          alt={file.name}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            backgroundColor: colors.background.light,
          }}
          loading="lazy"
          referrerPolicy="no-referrer"
        />

        {onRemove && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            size="small"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              width: 20,
              height: 20,
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
            }}
            aria-label="削除"
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}

        {canOpen && previewSrc && (
          <CustomFileModal
            open={previewOpen}
            onClose={handleClosePreview}
            imageSrc={previewSrc}
            imageName={file.name}
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: 50,
        borderRadius: "10px",
        backgroundColor: "#FFFFFF",
        px: 1.25,
        py: 0.75,
        border: "1px solid #E6E6E6",
        minWidth: 0,
        boxSizing: "border-box",
        cursor: canOpen ? "pointer" : "default",
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        "&:hover": canOpen
          ? {
              backgroundColor: "rgba(0, 0, 0, 0.02)",
              borderColor: "#D9D9D9",
            }
          : undefined,
      }}
      role={canOpen ? "button" : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onClick={handleFileClick}
      onKeyDown={(e) => {
        if (!canOpen) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleFileClick();
        }
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ minWidth: 0, flex: 1, maxWidth: "100%", pr: 1 }}
      >
        <DescriptionIcon
          sx={{ fontSize: 20, flexShrink: 0, color: primaryColor }}
          aria-hidden="true"
        />
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            gap: 0,
          }}
        >
          <CustomFileNameDisplay fileName={file.name} />
        </Box>
      </Stack>

      {onRemove && (
        <IconButton
          onClick={onRemove}
          size="small"
          sx={{
            width: 20,
            height: 20,
            color: colors.text.primary,
            flexShrink: 0,
            "&:hover": {
              backgroundColor: "rgba(62, 62, 62, 0.1)",
            },
          }}
          aria-label="削除"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}

      {canOpen && (isPdfFile || isImageFile) && previewSrc && (
        <CustomFileModal
          open={previewOpen}
          onClose={handleClosePreview}
          imageSrc={previewSrc}
          imageName={file.name}
        />
      )}
    </Box>
  );
};
